import {
  InboxListItemContent,
  InboxPreview,
  NodeTimeline,
  PollingSpinner,
  RenderNode,
  ViewInvoiceButton,
  WaitingForNextStepItem
} from 'Automations/components'
import { match } from 'ts-pattern'
import {
  Text,
  Badge,
  Button,
  GreyGrey80,
  Link,
  Primary14SemiBold,
  Box,
  Flex
} from '@sequencehq/core-components'
import { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'
import { getWorkflowDescription } from 'Automations/automationExecutions/utils/getWorkflowDescription'
import { getTasksGroupedByNode } from 'Automations/automationExecutions/overview/getAllTasksGroupedByNode'
import { resolvedNodeDefinitionsAtom } from 'Automations/nodeDefinitions'
import { AutomationExecutionKebabMenu } from 'Automations/automationExecutions/overview/AutomationExecutionKebabMenu'
import { useAutomationExecution } from 'Automations/hooks/useAutomationExecution'
import { useManualTasksByExecutionId } from 'Automations/watchtower/inbox/manualTasks/hooks/useManualTasksByExecutionId'
import { useAtomValue, useSetAtom } from 'jotai'
import { manualTaskProcessorTaskIdAtom } from 'Automations/watchtower/atoms'
import { useExecutionTimeline } from 'Automations/automationExecutions/overview/useExecutionTimeline'
import { CollapsibleToggleItem } from 'Automations/automationExecutions/overview/CollapsibleToggleItem'
import { AnimatePresence, motion } from 'framer-motion'
import { useMemo } from 'react'
import { TASK_EXECUTORS } from 'Automations/automations.constants'

export const AutomationExecutionSummary = (props: {
  automationExecutionId: string | null
}) => {
  const { isPending, isPolling, automationExecution, triggerType } =
    useAutomationExecution({
      executionId: props.automationExecutionId
    })

  const { manualTasks, isPending: manualTasksPending } =
    useManualTasksByExecutionId({
      executionId: props.automationExecutionId,
      executionTaskCount: automationExecution?.tasks.length
    })
  const setProcessorTaskId = useSetAtom(manualTaskProcessorTaskIdAtom)
  const resolvedNodeDefinitions = useAtomValue(resolvedNodeDefinitionsAtom)

  const reviewableTasks = useMemo(
    () => filterByActiveExecutionTasks(manualTasks, automationExecution),
    [manualTasks, automationExecution]
  )

  const nodes = automationExecution
    ? getTasksGroupedByNode(automationExecution)
    : []
  const {
    firstNode,
    collapsibleNodes,
    pinnedNodes,
    hasCollapsibleNodes,
    isExpanded,
    toggle,
    showWaitingItem
  } = useExecutionTimeline({
    nodes,
    isRunning: automationExecution?.status === 'RUNNING',
    reviewableTasks,
    executionId: props.automationExecutionId
  })

  // Gate on the resolved registry too: execution summaries are looked up from
  // it, and the async start-node entries are only concrete once resolved.
  if (isPending || !resolvedNodeDefinitions) {
    return <InboxPreview.LoadingState />
  }

  if (!automationExecution) {
    return <InboxPreview.EmptyState />
  }

  // A running execution may briefly have no grouped nodes yet; keep showing the
  // header and "Waiting for next step" rather than a blank empty state.
  if (nodes.length === 0 && automationExecution.status !== 'RUNNING') {
    return <InboxPreview.EmptyState />
  }

  const { title, logo } = getWorkflowDescription(automationExecution)
  const statusBadge = match(automationExecution.status)
    .with('COMPLETED', () => <Badge sentiment="success">Completed</Badge>)
    .with('TERMINATED', 'FAILED', () => (
      <Badge sentiment="error">Rejected</Badge>
    ))
    .with('RUNNING', () => null)
    .exhaustive()

  return (
    <InboxPreview
      header={
        <Flex alignItems="center" gap="8px">
          <InboxListItemContent.TaskIcon isSelected={false}>
            {logo}
          </InboxListItemContent.TaskIcon>
          <Text
            {...Primary14SemiBold}
            color={GreyGrey80}
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {title}
          </Text>
          {statusBadge}
        </Flex>
      }
      actions={
        /* Wait until the reviewable tasks have resolved before deciding which
           action to show, so we don't flash a fallback action (e.g. "View
           quote") that is then replaced by a review action (e.g. "Review
           quote"). */
        !manualTasksPending &&
        getTriggerActions({
          triggerType,
          automationExecution,
          reviewableTasks,
          onOpenProcessor: setProcessorTaskId
        })
      }
      headerActions={
        <AutomationExecutionKebabMenu
          automationExecutionId={automationExecution.id}
        />
      }
    >
      <Box position="absolute" top="8px" right="16px">
        <PollingSpinner isPolling={isPolling} />
      </Box>
      <NodeTimeline data-testid="watchtower.executionSummary.nodeTimeline">
        {firstNode && (
          <RenderNode
            node={firstNode}
            automationExecution={automationExecution}
            reviewableTasks={reviewableTasks}
            isLatestNodeExecution={
              !hasCollapsibleNodes &&
              pinnedNodes.length === 0 &&
              !showWaitingItem
            }
          />
        )}

        {hasCollapsibleNodes && (
          <>
            <AnimatePresence initial={false}>
              {isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.15, ease: 'easeInOut' }}
                  style={{ overflow: 'hidden' }}
                >
                  {collapsibleNodes.map((node, idx) => (
                    <RenderNode
                      key={node.nodeId}
                      node={node}
                      automationExecution={automationExecution}
                      reviewableTasks={reviewableTasks}
                      isLatestNodeExecution={
                        pinnedNodes.length === 0 &&
                        !showWaitingItem &&
                        idx === collapsibleNodes.length - 1
                      }
                    />
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
            <CollapsibleToggleItem
              isExpanded={isExpanded}
              onToggle={toggle}
              data-testid="watchtower.executionSummary.collapsibleToggle"
            />
          </>
        )}

        {pinnedNodes.map((node, idx) => (
          <RenderNode
            key={node.nodeId}
            node={node}
            automationExecution={automationExecution}
            reviewableTasks={reviewableTasks}
            isLatestNodeExecution={
              !showWaitingItem && idx === pinnedNodes.length - 1
            }
          />
        ))}

        {showWaitingItem && (
          <WaitingForNextStepItem data-testid="watchtower.executionSummary.waitingForNextStep" />
        )}
      </NodeTimeline>
    </InboxPreview>
  )
}

const getTriggerActions = ({
  triggerType,
  automationExecution,
  reviewableTasks,
  onOpenProcessor
}: {
  triggerType: Dashboardv99990101Api.Automations.TriggerType | undefined
  automationExecution: Dashboardv99990101Api.Automations.AutomationExecution
  reviewableTasks: Dashboardv99990101Api.Automations.ManualTask[]
  onOpenProcessor: (taskId: string) => void
}) => {
  return (
    match(triggerType)
      .with('QUOTE_REQUIRES_APPROVAL', () => {
        const quoteReviewTasks = reviewableTasks.filter(
          task => task.type === 'QUOTE_APPROVAL' && task.status === 'PENDING'
        )

        if (quoteReviewTasks.length === 1) {
          return (
            <Button
              data-testid="watchtower.executionSummary.reviewQuote"
              variant="primary"
              onClick={() => onOpenProcessor(quoteReviewTasks[0].id)}
            >
              Review quote
            </Button>
          )
        }

        return (
          <Button
            data-testid="watchtower.executionSummary.viewQuote"
            as={Link.Unstyled}
            to={`/quotes/${automationExecution.input?.quoteId}`}
            variant="secondary"
          >
            View quote
          </Button>
        )
      })
      .with('CONTRACT_UPLOADED', () => {
        const contractReviewTasks = reviewableTasks.filter(
          task => task.type === 'CONTRACT_REVIEW' && task.status === 'PENDING'
        )

        if (contractReviewTasks.length === 1) {
          return (
            <Button
              data-testid="watchtower.executionSummary.reviewSchedule"
              variant="primary"
              onClick={() => onOpenProcessor(contractReviewTasks[0].id)}
            >
              Review schedule
            </Button>
          )
        }

        const createScheduleTask = automationExecution.tasks.find(
          task =>
            task.inputData.taskExecutorConfiguration?.executor ===
            TASK_EXECUTORS.createSchedule
        )

        if (createScheduleTask?.outputData?.billingScheduleId) {
          return (
            <Button
              data-testid="watchtower.executionSummary.viewSchedule"
              as={Link.Unstyled}
              to={`/schedules/${createScheduleTask.outputData?.billingScheduleId}`}
              variant="secondary"
            >
              View schedule
            </Button>
          )
        }
      })
      .with('INVOICE_SENT', () => {
        const reviewEmailTasks = reviewableTasks.filter(
          task => task.type === 'REVIEW_EMAIL' && task.status === 'PENDING'
        )
        const invoiceId =
          typeof automationExecution.input?.invoiceId === 'string'
            ? automationExecution.input.invoiceId
            : undefined
        const showReviewButton = reviewEmailTasks.length === 1

        if (!invoiceId && !showReviewButton) {
          return null
        }

        return (
          <Flex gap="8px" alignItems="center">
            {invoiceId && (
              <ViewInvoiceButton
                invoiceId={invoiceId}
                data-testid="watchtower.executionSummary.viewInvoice"
              />
            )}
            {showReviewButton && (
              <Button
                data-testid="watchtower.executionSummary.reviewEmail"
                variant="primary"
                onClick={() => onOpenProcessor(reviewEmailTasks[0].id)}
              >
                Review reminder
              </Button>
            )}
          </Flex>
        )
      })
      // No CTA for unrecognised trigger types. Returning null (rather than a
      // label string) keeps the mobile bottom action bar from rendering an empty
      // bar with orphaned text.
      .otherwise(() => null)
  )
}

const ACTIVE_TASK_STATUSES =
  new Set<Dashboardv99990101Api.Automations.AutomationExecutionTaskStatus>([
    'IN_PROGRESS',
    'SCHEDULED'
  ])

const filterByActiveExecutionTasks = (
  manualTasks: Dashboardv99990101Api.Automations.ManualTask[],
  automationExecution:
    | Dashboardv99990101Api.Automations.AutomationExecution
    | null
    | undefined
): Dashboardv99990101Api.Automations.ManualTask[] => {
  if (!automationExecution) return []

  const activeTaskIds = new Set(
    automationExecution.tasks
      .filter(task => ACTIVE_TASK_STATUSES.has(task.status))
      .map(task => task.taskId)
  )

  return manualTasks.filter(mt => activeTaskIds.has(mt.executorTaskId))
}
